<script lang="ts" module>
  import { reactiveContext } from "#lib/internal/helpers"

  const { getContext, setContext } = reactiveContext({
    variant: null as VariantProps<typeof alertVariants>["variant"],
  })

  export const getAlertRootContext = getContext
</script>

<script lang="ts">
  // Toast notifications only. For inline notices use Banner.
  import type { BaseProps } from "#lib/internal/types"
  import { cn } from "#lib/utils"
  import { Close } from "@tastyworks/icons"
  import type { VariantProps } from "class-variance-authority"
  import type { Snippet } from "svelte"
  import Button from "../button/Button.svelte"
  import { Alert, alertVariants } from "."

  type Props = BaseProps<"div"> & {
    variant: VariantProps<typeof alertVariants>["variant"]
    show?: boolean
    icon?: Snippet
    children?: Snippet
    ondismiss?: () => void
  }

  let {
    class: className = undefined,
    variant,
    show = $bindable(true),
    icon,
    children,
    ondismiss,
    ...restProps
  }: Props = $props()

  const rootCtx = setContext({
    variant: (v) => {
      if (v !== variant) variant = v
    },
  })
  $effect(() => {
    rootCtx.set({ variant })
  })

  function handleDismiss() {
    show = false
    ondismiss?.()
  }
</script>

<div
  class={cn(alertVariants({ variant }), className)}
  {...restProps}
  class:hidden={!show}
  role="alert"
>
  <Button
    class="float-right ml-4 flex items-center justify-center p-0"
    variant="ghostPrimary"
    size="medium"
    aria-label="Dismiss alert"
    onclick={handleDismiss}
  >
    <Close class="h-6 w-6" />
  </Button>

  <div class="flex gap-3">
    {#if icon}
      {@render icon()}
    {:else}
      <Alert.Icon />
    {/if}

    <div class="flex flex-col gap-y-1">
      {@render children?.()}
    </div>
  </div>
</div>
